import React, {
    useContext,
    useEffect, useMemo, useState 
} from 'react';
import {
    Box, Typography, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';

export const AboutYouSection = (): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles(() => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        staticCheckmark: {
            width: '16.2px',
            height: '16.2px',
            margin: 'auto 0'
        },
        checkmark: {
            width: '80px',
            height: '80px',
            margin: '0 auto',
            transform: 'translate(0)',
            zIndex: 1,
            animation: `$moveCheckmark ${skipAnimation ? 0 : 0.5}s forwards 0s, $moveCheckmark2 ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s, $fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        sections: {
            height: '80px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            columnGap: '10px',
            marginTop: '50px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s`
        },
        section: {
            height: '80px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '8px'
        },
        background: {
            margin: 'auto 0',
            backgroundColor: '#C0C0C0',
            borderRadius: '50%',
            width: '16px',
            height: '16px',
        },
        aboutYouBackground: {
            margin: 'auto 0',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#C0C0C0',
            borderRadius: '50%',
            width: '16px',
            height: '16px',
            alignItems: 'center',
            justifyContent: 'center',
            animation: `$changeColor ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s, $semigrow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s, $grow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`,
        },
        icon: {
            fill: '#C0C0C0',
        },
        goalsSection: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        habitsSection: {
            opacity: 0,
        },
        aboutYouIcon: {
            opacity: 0,
            fill: '#F2BE59',
            width: '50%',
            height: '50%',
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`,
        },
        aboutYouSection: {
            opacity: 1,
        },
        planSection: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        targetSection: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        title: {
            fontWeight: TypographyWeights.BOLD,
            textAlign: 'center',
            marginBottom: '8px',
            marginTop: '50px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`
        },
        subtitle: {
            textAlign: 'center',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes changeColor': {
            '0%': {
                backgroundColor: '#C0C0C0' 
            },
            '100%': {
                backgroundColor: '#F2BE59' 
            },
        },
        '@keyframes semigrow': {
            '0%': {
                width: '16px',
                height: '16px' 
            },
            '100%': {
                width: '24px',
                height: '24px' 
            },
        },
        '@keyframes grow': {
            '0%': {
                width: '24px',
                height: '24px' 
            },
            '100%': {
                width: '80px',
                height: '80px' 
            },
        },
        '@keyframes moveCheckmark': {
            '0%': {
                transform: 'translate(0)',
                width: '80px',
                height: '80px' 
            },
            '100%': {
                transform: 'translate(-25px, 98px)',
                width: '16.2px',
                height: '16.2px' 
            },
        },
        '@keyframes moveCheckmark2': {
            '0%': {
                transform: 'translate(-25px, 98px)',
                width: '16.2px',
                height: '16.2px' 
            },
            '100%': {
                transform: 'translate(-30px, 98px)',
                width: '16.2px',
                height: '16.2px' 
            },
        }
    })), [skipAnimation]);
    const classes = useStyles();
    const theme = useTheme();

    useEffect(() => {
        setTimeout(() => {
            setSkipAnimation(true);
        }, 2500);
    }, []);

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    const onBackClick = () => {
        formContext.goBack();
    };

    return (
        <CardContainer
            sectionKey={'aboutYou'}
            goBack={onBackClick}
            goNext={onClick}
            backgroundColor={theme.palette.white}
            hideHeaderText
        >
            <Card>
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <svg
                            className={classes.checkmark}
                            width="82"
                            height="82"
                            viewBox="0.5 0.5 82 82"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle
                                cx="41"
                                cy="41"
                                r="40.5"
                                fill={theme.palette.neutral['400']}
                                stroke={theme.palette.neutral['400']}
                            />
                            <path
                                d="M20.1 47.2 l12.1 10.1 c0 0 1.7 1.7 2.9 -1.5 l25.4 -32.1"
                                stroke={theme.palette.white}
                                strokeWidth="7.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <Box className={classes.sections}>
                            <Box className={`${classes.section} ${classes.goalsSection}`}>
                                <svg
                                    className={classes.staticCheckmark}
                                    width="82"
                                    height="82"
                                    viewBox="0.5 0.5 82 82"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="41"
                                        cy="41"
                                        r="40.5"
                                        fill={theme.palette.neutral['400']}
                                        stroke={theme.palette.neutral['400']}
                                    />
                                    <path
                                        d="M20.1 47.2 l12.1 10.1 c0 0 1.7 1.7 2.9 -1.5 l25.4 -32.1"
                                        stroke={theme.palette.white}
                                        strokeWidth="7.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Box>
                            <Box className={`${classes.section} ${classes.habitsSection}`}>
                                <Box className={classes.background} />
                            </Box>
                            <Box className={`${classes.section} ${classes.aboutYouSection}`}>
                                <Box className={classes.aboutYouBackground}>
                                    <svg
                                        className={`${classes.icon} ${classes.aboutYouIcon}`}
                                        width="40"
                                        height="40"
                                        viewBox="0 0 40 40"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clipPath="url(#clip0_2204_62973)">
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M11.9531 16.7271C11.9531 12.2837 15.5552 8.68164 19.9986 8.68164C24.442 8.68164 28.044 12.2837 28.044 16.7271V19.9998C28.044 24.4432 24.442 28.0453 19.9986 28.0453C15.5552 28.0453 11.9531 24.4432 11.9531 19.9998V16.7271ZM19.9986 11.6816C17.2121 11.6816 14.9531 13.9406 14.9531 16.7271V19.9998C14.9531 22.7864 17.2121 25.0453 19.9986 25.0453C22.7851 25.0453 25.044 22.7864 25.044 19.9998V16.7271C25.044 13.9406 22.7851 11.6816 19.9986 11.6816Z"
                                                fill={theme.palette.white}
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M20 0.5C9.23045 0.5 0.5 9.23045 0.5 20C0.5 30.7696 9.23045 39.5 20 39.5C30.7696 39.5 39.5 30.7696 39.5 20C39.5 9.23045 30.7696 0.5 20 0.5ZM3.5 20C3.5 10.8873 10.8873 3.5 20 3.5C29.1127 3.5 36.5 10.8873 36.5 20C36.5 23.1634 35.6098 26.1189 34.0661 28.6296L30.4876 26.8403C29.7466 26.4698 28.8456 26.7702 28.4751 27.5111C28.1046 28.2521 28.4049 29.1531 29.1459 29.5236L32.2365 31.0689C29.2174 34.4045 24.8534 36.5 20 36.5C15.1466 36.5 10.7826 34.4045 7.76345 31.0689L10.8541 29.5236C11.5951 29.1531 11.8954 28.2521 11.5249 27.5111C11.1544 26.7702 10.2534 26.4698 9.51245 26.8403L5.93385 28.6296C4.39024 26.1189 3.5 23.1634 3.5 20Z"
                                                fill={theme.palette.white}
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2204_62973">
                                                <rect
                                                    width="40"
                                                    height="40"
                                                    fill={theme.palette.white}
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </Box>
                            </Box>
                            <Box className={`${classes.section} ${classes.planSection}`}>
                                <Box className={classes.background} />
                            </Box>
                            <Box className={`${classes.section} ${classes.targetSection}`}>
                                <Box className={classes.background} />
                            </Box>
                        </Box>
                        <Typography
                            variant={TypographyVariants.H2}
                            className={classes.title}
                        >
                            {'About You'}
                        </Typography>
                        <Typography
                            variant={TypographyVariants.H5}
                            className={classes.subtitle}
                        >
                            {'Now, tell us a little more about yourself to help us personalize and refine your custom plan.'}
                        </Typography>
                    </Box>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default AboutYouSection;
